import { decrypt, encrypt } from "../Core/utils/others";
import { checkIfPaidUser, getRole } from "../Core/utils/auth";
import { fireSourceAwareGtm } from "./gtm";

/**
 * Paid students choose between the new homepage (SPDashboard, shared with
 * StudentPortal) and the old Sandbox one (HomePage/Index). The choice and the
 * announcement banner's timer both live in localStorage, keyed by user id so
 * two students sharing a device each keep their own.
 */

export const HOMEPAGE_VERSION = { NEW: "new", OLD: "old" };

export const HOMEPAGE_VERSION_EVENT = "sandbox_homepage_version_update";

export const HOMEPAGE_BANNER_EVENT = "sandbox_homepage_banner_update";

export const HOMEPAGE_VERSION_KEY = "sandbox_homepage_version";
export const HOMEPAGE_BANNER_KEY = "sandbox_homepage_banner";

export const BANNER_DURATION_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * localStorage.clear() for logout and session expiry that keeps the homepage
 * choice and the banner's counters. Both are maps keyed by user id, so keeping
 * them leaks nothing between students sharing a device — and wiping them would
 * hand a student who chose the old homepage the new one again, and restart the
 * banner's three days and three closes on every login.
 */
export const clearLocalStorageKeepingHomepageChoice = () => {
  const kept = [HOMEPAGE_VERSION_KEY, HOMEPAGE_BANNER_KEY].map((key) => [
    key,
    localStorage.getItem(key),
  ]);
  localStorage.clear();
  kept.forEach(([key, value]) => {
    if (value !== null) localStorage.setItem(key, value);
  });
};

export const BANNER_MAX_DISMISSALS = 3;

/** What the switch is for, shown beside it. */
export const HOMEPAGE_SWITCH_NAME = "New Sandbox experience";

/**
 * The other end of the switch. It names the setting rather than the next click,
 * so it stays put while the knob moves between the two: right for the new
 * experience, left for the old one.
 */
export const HOMEPAGE_SWITCH_ACTION = "Switch to old version";

const ANONYMOUS_USER = "anonymous";

const readStore = (key) => {
  try {
    return decrypt(localStorage.getItem(key)) || null;
  } catch (_) {
    return null;
  }
};

const getCurrentUserId = () => {
  const user = readStore("sandbox_user");
  return String(user?.user_id || user?.userId || ANONYMOUS_USER);
};

const writeUserEntry = (key, value) => {
  try {
    const store = readStore(key) || {};
    store[getCurrentUserId()] = value;
    localStorage.setItem(key, encrypt(store));
  } catch (_) {
    // storage unavailable (private mode, quota) — the choice just won't persist
  }
};

/** Sandbox mounted inside StudentPortal — set by the portal before this bundle loads. */
export const isEmbeddedInPortal = () =>
  typeof window !== "undefined" && window.__SANDBOX_IS_PLUGIN__ === true;

/** The banner and the switch are for paid students on standalone Sandbox only. */
export const isHomepageChoiceAvailable = () =>
  getRole() === "student" && checkIfPaidUser() && !isEmbeddedInPortal();

/** @returns {"new"|"old"} the current student's homepage, defaulting to the new one */
export const readHomepageVersion = () => {
  const stored = readStore(HOMEPAGE_VERSION_KEY)?.[getCurrentUserId()];
  return stored === HOMEPAGE_VERSION.OLD
    ? HOMEPAGE_VERSION.OLD
    : HOMEPAGE_VERSION.NEW;
};

/**
 * Persists the choice and tells every mounted reader — App's route table, the
 * banner and the nav switches — to re-read it.
 * @param {"new"|"old"} version
 */
export const writeHomepageVersion = (version) => {
  writeUserEntry(HOMEPAGE_VERSION_KEY, version);
  window.dispatchEvent(new Event(HOMEPAGE_VERSION_EVENT));
};

let isBannerShowing = false;

/**
 * The banner reports whether it is on screen right now (mounted, not expired,
 * not closed this visit). PaidStudentHome only preloads the other homepage
 * while it is: after that, a switch is rare enough to load it on demand.
 * @param {boolean} showing
 */
export const setHomepageBannerShowing = (showing) => {
  if (isBannerShowing === showing) return;
  isBannerShowing = showing;
  window.dispatchEvent(new Event(HOMEPAGE_BANNER_EVENT));
};

/** @returns {boolean} whether the upgrade banner is on screen right now */
export const isHomepageBannerShowing = () => isBannerShowing;

/** @returns {{ firstSeenAt: number|null, dismissCount: number }} */
export const readBannerState = () => {
  const entry = readStore(HOMEPAGE_BANNER_KEY)?.[getCurrentUserId()];
  return {
    firstSeenAt: Number(entry?.firstSeenAt) || null,
    dismissCount: Number(entry?.dismissCount) || 0,
  };
};

/** @param {{ firstSeenAt: number|null, dismissCount: number }} state */
export const writeBannerState = (state) =>
  writeUserEntry(HOMEPAGE_BANNER_KEY, state);

/**
 * The banner shows from the first time the student sees it until three days
 * later, or until they have closed it three times — whichever comes first.
 * Closing it hides it for that visit only; it comes back on the next one.
 * @param {{ firstSeenAt: number|null, dismissCount: number }} state
 * @param {number} now - epoch ms
 */
export const isBannerActive = (state, now) => {
  if ((state?.dismissCount ?? 0) >= BANNER_MAX_DISMISSALS) return false;
  if (!state?.firstSeenAt) return true;
  return now - state.firstSeenAt < BANNER_DURATION_MS;
};

/**
 * GTM event for the homepage choice, labelled with the student's email like the
 * rest of the dashboard's tracking.
 * @param {string} action
 * @param {Object} [extraLabels]
 */
export const fireHomepageGtm = (action, extraLabels = {}) => {
  const user = readStore("sandbox_user");
  fireSourceAwareGtm(
    action,
    user?.email || user?.studentEmail || "",
    extraLabels,
  );
};
